// @ts-check

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import I18n from 'i18n-js'
import { useCallback } from 'react'

/**
 * @type {React.FC<Omit<ConfirmDialogProps, 'onClose'> & { onClose: (didConfirm: boolean) => void }>}
 */
const ConfirmDialog = (props) => {
  const handleCancelClick = useCallback(() => {
    if (props.onCancelClick) {
      props.onCancelClick()
    }

    props.onClose(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onClose, props.onCancelClick])

  const handleConfirmClick = useCallback(() => {
    if (props.onConfirmClick) {
      props.onConfirmClick()
    }

    props.onClose(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onClose, props.onConfirmClick])

  const handleOnClose = useCallback(() => {
    props.onClose(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onClose])

  const content =
    typeof props.content === 'string' || typeof props.content === 'number' ? (
      <DialogContentText>{props.content}</DialogContentText>
    ) : (
      props.content
    )

  return (
    <Dialog fullWidth onClose={handleOnClose} open={props.open}>
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        <Button onClick={handleCancelClick}>{props.cancelLabel}</Button>
        <Button
          color="primary"
          onClick={handleConfirmClick}
          variant="contained"
        >
          {props.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ConfirmDialog.defaultProps = {
  title: I18n.t('shared.areYouSure'),
  content: I18n.t('shared.cannotBeUndone'),
  cancelLabel: I18n.t('shared.cancel'),
  confirmLabel: I18n.t('shared.ok'),
}

/**
 * @typedef {{
 *  open: boolean
 *  onClose: () => void
 *  onCancelClick?: () => void
 *  onConfirmClick?: () => void
 *  cancelLabel?: React.ReactNode
 *  confirmLabel?: React.ReactNode
 *  content?: React.ReactNode
 *  title?: React.ReactNode,
 * }} ConfirmDialogProps
 */

export default ConfirmDialog
